import { useCallback, useMemo } from "react";

import { GetUserFavoriteListQuery } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { userFavoritesQueryDocument } from "./user-favorite.gql";

export const useGetUserFavoriteList = ({ workspaceId }: { workspaceId: string }) => {
  const queryResult = useQuery({
    queryKey: queryKeys.users.details(), // We'll need to add user favorites query key
    queryFn: async (): Promise<GetUserFavoriteListQuery> => {
      const client = createOperationClient("getUserFavoriteList");
      return await client.request(userFavoritesQueryDocument, {
        getUserFavoriteListInput: { workspaceId },
      });
    },
  });

  const favoriteList = useMemo(() => {
    return queryResult.data?.getUserFavoriteList || [];
  }, [queryResult.data?.getUserFavoriteList]);

  const getFavoriteByPageUrl = useCallback(
    ({ pageUrl }: { pageUrl: string }) => {
      return favoriteList.find((favorite) => favorite.pageUrl === pageUrl);
    },
    [favoriteList],
  );

  const isFavorite = useCallback(
    ({ pageUrl }: { pageUrl: string }) => {
      return favoriteList.some((favorite) => favorite.pageUrl === pageUrl);
    },
    [favoriteList],
  );

  return {
    favoriteList,
    getFavoriteByPageUrl,
    isFavorite,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
